import { useEffect, useRef, useState } from "react";

import {
  Agent,
  UserProposalRequest,
  UserProposalResponse,
  UserProposalWithRecommendationsResponse,
  UserOfferSourceAnalysisItemSourceEnum,
} from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";

const SESSION_STORAGE_SOURCE_KEY = "agent_search_source";

const rewriteSourceForLearningArticleIfNeeded = (source?: string): string | undefined => {
  if (typeof window === "undefined") return source;
  const stored = window.sessionStorage.getItem(SESSION_STORAGE_SOURCE_KEY);
  if (stored !== AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE) {
    return source;
  }

  window.sessionStorage.removeItem(SESSION_STORAGE_SOURCE_KEY);

  if (!source) return source;

  switch (source) {
    case UserOfferSourceAnalysisItemSourceEnum.AgentSearch:
    case UserOfferSourceAnalysisItemSourceEnum.SearchResult:
      return UserOfferSourceAnalysisItemSourceEnum.LearningArticle;
    case UserOfferSourceAnalysisItemSourceEnum.AgentSearchViaDetail:
    case UserOfferSourceAnalysisItemSourceEnum.SearchResultViaDetail:
      return UserOfferSourceAnalysisItemSourceEnum.LearningArticleViaDetail;
    default:
      return source;
  }
};

// 「対象外と確定した」場合だけ true を返す。null / undefined（判定不能）は undefined のままにして、
// 呼び出し側が対象外向けの出し分けに倒れないようにする
const toOutOfTargetCondition = (isTarget?: boolean | null): boolean | undefined =>
  isTarget === null || isTarget === undefined ? undefined : isTarget === false;

export interface CreateUserProposalResult {
  ok: boolean;
  additionalAgents: Agent[];
  isTarget: boolean;
  // 対象条件に合致しないと確定した（is_target が明示的に false）マッチかどうか。
  // API が is_target を返さない場合は判定不能なので undefined のままにし、
  // 呼び出し側は従来どおり（日程選択あり）の扱いに倒す
  isOutOfTargetCondition?: boolean;
  // セッション切れは再試行しても回復しないため、呼び出し側が再ログイン導線へ出し分けられるよう区別して返す
  isUnauthorized: boolean;
  // 作成された UserProposal の ID。マッチ成立後の希望連絡日程 PATCH に必要。
  // レスポンスに含まれない場合は undefined になり、呼び出し側は日程送信を配線しない
  userProposalId?: number;
}

interface UseCreateUserProposal {
  createUserProposal: (request: UserProposalRequest) => Promise<CreateUserProposalResult>;
  loading: boolean;
  error: string;
}

export const useCreateUserProposal = (): UseCreateUserProposal => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // 送信中に画面を離れうるため、アンマウント後に応答が届いても setState しないようガードする。
  // React 17 の「unmounted component への setState」警告は console.error で出るため、
  // CaptureConsole 経由で Sentry イベントになってしまう
  const mountedRef = useRef(true);
  useEffect(() => {
    // effect が張り直されたときに false のまま残らないよう、本体で戻す
    // （React 17 かつ StrictMode 無しの現状は不要だが、Fast Refresh と将来の React 18 化への保険）
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const fp = PropallyApiFactory();

  const createUserProposal = async (request: UserProposalRequest): Promise<CreateUserProposalResult> => {
    setLoading(true);
    setError("");

    try {
      const patchedRequest: UserProposalRequest = {
        ...request,
        source: rewriteSourceForLearningArticleIfNeeded(request.source),
      };

      // OpenAPI 生成タイミング差分を吸収:
      // withRecommendations エンドポイントがある場合は追加おすすめ担当者を受け取り、
      // ない場合は通常エンドポイントにフォールバックする。
      const hasWithRecommendationsEndpoint = typeof (fp as any).v1UserProposalsWithRecommendationsPost === "function";
      if (hasWithRecommendationsEndpoint) {
        const res = await (fp as any).v1UserProposalsWithRecommendationsPost(patchedRequest, {
          withCredentials: true,
        });
        const responseData = (res?.data ?? {}) as UserProposalWithRecommendationsResponse;
        const additionalAgents = Array.isArray(responseData?.recommend_agents) ? responseData.recommend_agents : [];
        return {
          ok: res.status >= 200 && res.status < 300,
          additionalAgents,
          isTarget: responseData.is_target === true,
          isOutOfTargetCondition: toOutOfTargetCondition(responseData.is_target),
          isUnauthorized: false,
          userProposalId: responseData.id,
        };
      }

      const res = await fp.v1UserProposalsPost(patchedRequest, { withCredentials: true });
      const responseData: UserProposalResponse = res.data;
      return {
        ok: res.status >= 200 && res.status < 300,
        additionalAgents: [],
        isTarget: responseData.is_target === true,
        isOutOfTargetCondition: toOutOfTargetCondition(responseData.is_target),
        isUnauthorized: false,
        userProposalId: responseData.id,
      };
    } catch (e: any) {
      // Sentry へは CustomApiFactory の interceptor が既に送っており、ここで console.error すると
      // CaptureConsole(levels: error) が同じ失敗を二重に拾うため、あえてログを出さない
      if (mountedRef.current) setError(e?.message || "不明なエラー");
      return { ok: false, additionalAgents: [], isTarget: false, isUnauthorized: e?.response?.status === 401 };
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return { createUserProposal, loading, error };
};
